import type * as monaco from 'monaco-editor';
import React, {useEffect, useRef} from 'react';

import {isBrowser} from 'lib/Browser';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {coerceToString} from 'util/coerceToString';

import {MonacoLanguage, MonacoTheme} from './MonacoTypes';
import useMonaco from './useMonaco';

const EDITOR_DEFAULTS: monaco.editor.IStandaloneEditorConstructionOptions = {
  automaticLayout: true,
  scrollBeyondLastLine: false,
  wordWrap: 'on',
};

export interface MonacoEditorProps {
  width: string | number;
  height: string | number;
  value: string;
  language: MonacoLanguage;
  options?: monaco.editor.IStandaloneEditorConstructionOptions;
  editorDidMount?: (editor: monaco.editor.IStandaloneCodeEditor) => void;
  editorWillUnmount?: (editor: monaco.editor.IStandaloneCodeEditor) => void;
  onChange?: (value: string) => void;
  onChangeContentSize?: (e: monaco.editor.IContentSizeChangedEvent) => void;
  className?: string;
  theme: MonacoTheme;
}

export default function MonacoEditor({
  width,
  height,
  value,
  language,
  options,
  editorDidMount,
  editorWillUnmount,
  onChange,
  onChangeContentSize,
  className,
  theme,
}: MonacoEditorProps): JSX.Element {
  // Guard against non-string values reaching monaco.editor.createModel, which
  // crashes if value is not a string (MONOLITH-CLIENT-28GR).
  const safeValue = coerceToString(value);
  const monaco = useMonaco();
  const containerElement = useRef<HTMLDivElement | null>(null);
  const editor = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const onDidChangeContentSubscription = useRef<monaco.IDisposable | null>(null);
  const onDidContentSizeChangeSubscription = useRef<monaco.IDisposable | null>(null);
  const shouldPreventOnChange = useRef<boolean | null>(null);

  const handleEditorDidMount = (): void => {
    editorDidMount?.(editor.current!);

    onDidChangeContentSubscription.current = editor.current!.onDidChangeModelContent(() => {
      if (!shouldPreventOnChange.current) {
        onChange?.(editor.current!.getValue());
      }
    });

    onDidContentSizeChangeSubscription.current = editor.current!.onDidContentSizeChange(
      (e: monaco.editor.IContentSizeChangedEvent) => {
        onChangeContentSize?.(e);
      }
    );
  };

  const handleEditorWillUnmount = (): void => {
    editorWillUnmount?.(editor.current!);
  };

  useEffect(
    () => {
      if (containerElement.current && monaco) {
        editor.current = monaco.editor.create(containerElement.current, {
          model: monaco.editor.createModel(safeValue, language),
          ...EDITOR_DEFAULTS,
          theme,
          ...options,
        });

        handleEditorDidMount();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monaco]
  );

  useEffect(() => {
    if (editor.current) {
      editor.current.updateOptions({
        automaticLayout: true,
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        ...options,
      });
    }
  }, [className, options]);

  useEffect(() => {
    if (editor.current && monaco) {
      const model = editor.current.getModel();
      monaco.editor.setModelLanguage(model!, language);
    }
  }, [language, monaco]);

  useEffect(() => {
    if (monaco) {
      monaco.editor.setTheme(theme);
    }
  }, [theme, monaco]);

  useEffect(() => {
    if (editor.current) {
      if (safeValue === editor.current.getValue()) {
        return;
      }

      const model = editor.current.getModel()!;
      shouldPreventOnChange.current = true;
      editor.current.pushUndoStop();
      // pushEditOperations says it expects a cursorComputer, but doesn't seem to need one
      // @ts-expect-error
      model.pushEditOperations(
        [],
        [
          {
            range: model.getFullModelRange(),
            text: safeValue,
          },
        ]
      );
      editor.current.pushUndoStop();
      shouldPreventOnChange.current = false;
    }
  }, [safeValue]);

  useEffect(
    () => () => {
      if (editor.current) {
        handleEditorWillUnmount();
        editor.current.dispose();
      }
      if (onDidChangeContentSubscription.current) {
        onDidChangeContentSubscription.current.dispose();
      }
      if (onDidContentSizeChangeSubscription.current) {
        onDidContentSizeChangeSubscription.current.dispose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!isBrowser) {
    // monaco editor is not supported outside of browser contexts e.g. node
    // to make this work in the context of unit tests fall back to a textarea
    return (
      <textarea
        value={safeValue}
        readOnly={options?.readOnly ?? false}
        onChange={(e) => {
          onChange?.(e.target.value);
        }}
      />
    );
  }

  if (!monaco) {
    // show loading animation while we load the monaco bundle
    return <CenteredHorizontalThrobber />;
  }

  return <div ref={containerElement} className={className} style={{height, width}} />;
}
